/*
 * Garaufi fit guide
 * Recommends a size from the product's own measurement chart.
 *
 * The chart values are garment measurements and their cut varies a lot between
 * products (a shapewear bodysuit is measured flat and unstretched, an oversized
 * tee has 25 cm of room). So instead of assuming one fixed allowance, we read the
 * intended allowance out of the product's own chart: for every row we compare the
 * garment value against the standard body measurement for that size, average the
 * difference, and treat that average as "how this piece is meant to sit".
 * The shopper's fit preference then nudges that target a few centimetres.
 */
(function () {
  'use strict';

  // Standard women's body measurements per size (cm), EU/FR sizing.
  var BODY = {
    'XS':    { bust: 82, waist: 64, hips: 90 },
    'S':     { bust: 86, waist: 68, hips: 94 },
    'M':     { bust: 90, waist: 72, hips: 98 },
    'L':     { bust: 94, waist: 76, hips: 102 },
    'XL':    { bust: 98, waist: 80, hips: 106 },
    'XXL':   { bust: 102, waist: 84, hips: 110 },
    '2XL':   { bust: 102, waist: 84, hips: 110 },
    'S/M':   { bust: 88, waist: 70, hips: 96 },
    'L/XL':  { bust: 96, waist: 78, hips: 104 }
  };

  // FR clothing size -> canonical letter size.
  var FR_TO_LETTER = { '34': 'XS', '36': 'S', '38': 'M', '40': 'L', '42': 'XL', '44': 'XXL' };

  // Children's sizes: age label -> body height / chest (cm).
  var KIDS_BODY = {
    '2': { height: 92, bust: 53 },
    '4': { height: 104, bust: 57 },
    '6': { height: 116, bust: 61 },
    '8': { height: 128, bust: 65 }
  };

  // How far the target allowance moves per fit preference, in cm.
  var PREFERENCE = {
    fitted:  { bust: -3.5, waist: -2.5 },
    regular: { bust: 0, waist: 0 },
    loose:   { bust: 4.5, waist: 3 }
  };

  var STORE_KEY = 'garaufi:fit-profile';

  function readProfile() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY)) || {}; }
    catch (e) { return {}; }
  }
  function writeProfile(patch) {
    try {
      var next = Object.assign(readProfile(), patch);
      localStorage.setItem(STORE_KEY, JSON.stringify(next));
    } catch (e) { /* private browsing — the quiz still works, it just won't remember */ }
  }

  function num(value) {
    var n = parseFloat(String(value).replace(',', '.'));
    return isFinite(n) ? n : null;
  }

  function normaliseSize(label) {
    return String(label).toUpperCase().replace(/\s+/g, '');
  }

  function bodyForRow(row, isKids) {
    var key = normaliseSize(row.ref || row.label);
    return isKids ? KIDS_BODY[key] : BODY[key];
  }

  /* Average allowance the designer built into this chart, per dimension. */
  function chartAllowance(sizes, isKids) {
    var out = {};
    ['bust', 'waist'].forEach(function (dim) {
      var diffs = [];
      sizes.forEach(function (row) {
        var body = bodyForRow(row, isKids);
        if (body && typeof row[dim] === 'number' && typeof body[dim] === 'number') {
          diffs.push(row[dim] - body[dim]);
        }
      });
      if (diffs.length) {
        out[dim] = diffs.reduce(function (a, b) { return a + b; }, 0) / diffs.length;
      }
    });
    return out;
  }

  /* Which body measurements this chart can actually use. */
  function usableDimensions(sizes) {
    var dims = [];
    ['bust', 'waist'].forEach(function (dim) {
      if (sizes.some(function (r) { return typeof r[dim] === 'number'; })) dims.push(dim);
    });
    return dims;
  }

  function recommend(chart, body, preference) {
    var isKids = chart.category.indexOf('kids') === 0;
    var sizes = chart.sizes;
    var allowance = chartAllowance(sizes, isKids);
    var offsets = PREFERENCE[preference] || PREFERENCE.regular;
    var dims = usableDimensions(sizes).filter(function (d) { return typeof body[d] === 'number'; });
    if (!dims.length) return null;

    // Waist leads on bottoms, bust leads everywhere else.
    var weights = chart.category.indexOf('bottom') > -1
      ? { waist: 1, bust: 0.4 }
      : { bust: 1, waist: 0.45 };

    var scored = sizes.map(function (row) {
      var total = 0, weight = 0, detail = {};
      dims.forEach(function (dim) {
        if (typeof row[dim] !== 'number') return;
        var target = (allowance[dim] || 0) + (offsets[dim] || 0);
        var actual = row[dim] - body[dim];
        detail[dim] = actual;
        total += Math.abs(actual - target) * weights[dim];
        weight += weights[dim];
      });
      return { row: row, score: weight ? total / weight : Infinity, ease: detail };
    }).filter(function (s) { return isFinite(s.score); });

    if (!scored.length) return null;
    scored.sort(function (a, b) { return a.score - b.score; });

    var best = scored[0];
    var runnerUp = scored[1] || null;
    var margin = runnerUp ? runnerUp.score - best.score : Infinity;

    return {
      size: best.row.label,
      row: best.row,
      ease: best.ease,
      alternative: margin < 1.5 && runnerUp ? runnerUp.row.label : null,
      confidence: best.score > 6 ? 'low' : (margin < 1.5 ? 'medium' : 'high'),
      dims: dims
    };
  }

  function recommendKidsByHeight(chart, height) {
    var best = null, bestGap = Infinity;
    chart.sizes.forEach(function (row) {
      var body = KIDS_BODY[normaliseSize(row.ref || row.label)];
      if (!body) return;
      var gap = Math.abs(body.height - height);
      // A child between two sizes is better off in the larger one.
      if (height > body.height) gap -= 3;
      if (gap < bestGap) { bestGap = gap; best = row; }
    });
    if (!best) return null;
    return { size: best.label, row: best, ease: {}, alternative: null,
             confidence: bestGap < 10 ? 'high' : 'medium', dims: [] };
  }

  var TEMPLATE = [
    '<div class="gfq-overlay" data-gfq-overlay hidden>',
    '  <div class="gfq-panel" role="dialog" aria-modal="true" aria-labelledby="{{id}}-title" data-gfq-panel>',
    '    <button type="button" class="gfq-close" data-gfq-dismiss aria-label="{{close}}">&times;</button>',
    '    <p class="gfq-eyebrow">{{eyebrow}}</p>',
    '    <h2 class="gfq-title" id="{{id}}-title" data-gfq-heading></h2>',
    '    <p class="gfq-sub" data-gfq-subheading></p>',
    '    <div class="gfq-progress" data-gfq-progress></div>',
    '    <div data-gfq-body></div>',
    '  </div>',
    '</div>'
  ].join('');

  var FitQuiz = function (root) {
    this.root = root;
    this.chart = JSON.parse(root.querySelector('[data-gfq-chart]').textContent);
    this.text = JSON.parse(root.querySelector('[data-gfq-strings]').textContent);
    this.isKids = this.chart.category.indexOf('kids') === 0;
    this.id = root.id || ('gfq-' + Math.random().toString(36).slice(2, 8));
    this.answers = { preference: 'regular' };
    this.stepIndex = 0;
    this.build();
  };

  FitQuiz.prototype.t = function (key) {
    return this.text[key] != null ? this.text[key] : key;
  };

  FitQuiz.prototype.build = function () {
    var html = TEMPLATE
      .replace(/\{\{id\}\}/g, this.id)
      .replace('{{close}}', this.t('close'))
      .replace('{{eyebrow}}', this.t('eyebrow'));
    var stale = document.querySelector('[data-gfq-holder="' + this.id + '"]');
    if (stale) stale.parentNode.removeChild(stale);
    var holder = document.createElement('div');
    holder.className = 'gfq';
    holder.setAttribute('data-gfq-holder', this.id);
    holder.innerHTML = html;
    document.body.appendChild(holder);

    this.overlay = holder.querySelector('[data-gfq-overlay]');
    this.panel = holder.querySelector('[data-gfq-panel]');
    this.heading = holder.querySelector('[data-gfq-heading]');
    this.subheading = holder.querySelector('[data-gfq-subheading]');
    this.progress = holder.querySelector('[data-gfq-progress]');
    this.body = holder.querySelector('[data-gfq-body]');
    this.holder = holder;

    var self = this;
    this.overlay.addEventListener('click', function (event) {
      if (event.target === self.overlay) self.close();
    });
    holder.querySelector('[data-gfq-dismiss]').addEventListener('click', function () { self.close(); });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && self.isOpen) self.close();
    });
  };

  FitQuiz.prototype.steps = function () {
    if (this.isKids) return ['kids', 'result'];
    if (this.answers.mode === 'measurements') return ['mode', 'measurements', 'preference', 'result'];
    if (this.answers.mode === 'usual') return ['mode', 'usual', 'preference', 'result'];
    return ['mode', 'measurements', 'preference', 'result'];
  };

  FitQuiz.prototype.open = function () {
    var saved = readProfile();
    if (!this.isKids) {
      ['bust', 'waist', 'height', 'usualSize', 'preference', 'mode'].forEach(function (key) {
        if (saved[key] != null && this.answers[key] == null) this.answers[key] = saved[key];
      }, this);
    }
    this.isOpen = true;
    this.previousFocus = document.activeElement;
    this.overlay.hidden = false;
    this.overlay.setAttribute('data-open', 'true');
    document.documentElement.style.overflow = 'hidden';
    this.stepIndex = 0;
    this.render();
  };

  FitQuiz.prototype.close = function () {
    this.isOpen = false;
    this.overlay.removeAttribute('data-open');
    this.overlay.hidden = true;
    document.documentElement.style.overflow = '';
    if (this.previousFocus && this.previousFocus.focus) this.previousFocus.focus();
  };

  FitQuiz.prototype.go = function (delta) {
    this.stepIndex = Math.max(0, Math.min(this.steps().length - 1, this.stepIndex + delta));
    this.render();
  };

  FitQuiz.prototype.render = function () {
    var steps = this.steps();
    var step = steps[this.stepIndex];

    this.progress.innerHTML = steps.map(function (_, i) {
      return '<span data-on="' + (i <= this.stepIndex) + '"></span>';
    }, this).join('');

    this['render_' + step]();

    var focusable = this.panel.querySelector('input, button:not([data-gfq-dismiss])');
    if (focusable) focusable.focus({ preventScroll: true });
  };

  FitQuiz.prototype.setHead = function (title, sub) {
    this.heading.textContent = title;
    this.subheading.textContent = sub || '';
    this.subheading.hidden = !sub;
  };

  FitQuiz.prototype.render_mode = function () {
    this.setHead(this.t('mode_title'), this.t('mode_sub'));
    var self = this;
    this.body.innerHTML =
      '<div class="gfq-cards">' +
      '  <button type="button" class="gfq-card" data-mode="measurements">' +
      '    <strong>' + this.t('mode_measure') + '</strong><span>' + this.t('mode_measure_sub') + '</span></button>' +
      '  <button type="button" class="gfq-card" data-mode="usual">' +
      '    <strong>' + this.t('mode_usual') + '</strong><span>' + this.t('mode_usual_sub') + '</span></button>' +
      '</div>';
    this.body.querySelectorAll('[data-mode]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.answers.mode = btn.getAttribute('data-mode');
        writeProfile({ mode: self.answers.mode });
        self.go(1);
      });
    });
  };

  FitQuiz.prototype.render_measurements = function () {
    this.setHead(this.t('measure_title'), this.t('measure_sub'));
    var dims = usableDimensions(this.chart.sizes);
    var self = this;
    var fields = dims.map(function (dim) {
      return '<div class="gfq-field">' +
        '<label for="' + self.id + '-' + dim + '">' + self.t(dim + '_label') +
        '<span class="gfq-hint">' + self.t(dim + '_hint') + '</span></label>' +
        '<div class="gfq-input"><input id="' + self.id + '-' + dim + '" type="number" inputmode="decimal" ' +
        'min="40" max="200" step="0.5" name="' + dim + '" value="' + (self.answers[dim] != null ? self.answers[dim] : '') + '">' +
        '<span class="gfq-unit">cm</span></div></div>';
    }).join('');

    this.body.innerHTML = fields +
      '<p class="gfq-error" data-gfq-error hidden></p>' +
      '<div class="gfq-actions">' +
      '  <button type="button" class="gfq-btn gfq-btn--ghost" data-gfq-back>' + this.t('back') + '</button>' +
      '  <button type="button" class="gfq-btn" data-gfq-next>' + this.t('next') + '</button>' +
      '</div>';

    this.body.querySelector('[data-gfq-back]').addEventListener('click', function () { self.go(-1); });
    this.body.querySelector('[data-gfq-next]').addEventListener('click', function () {
      var filled = 0, patch = {};
      dims.forEach(function (dim) {
        var value = num(self.body.querySelector('[name="' + dim + '"]').value);
        if (value && value >= 40 && value <= 200) { self.answers[dim] = value; patch[dim] = value; filled++; }
        else { delete self.answers[dim]; }
      });
      var error = self.body.querySelector('[data-gfq-error]');
      if (!filled) { error.textContent = self.t('error_measure'); error.hidden = false; return; }
      writeProfile(patch);
      self.go(1);
    });
  };

  FitQuiz.prototype.render_usual = function () {
    this.setHead(this.t('usual_title'), this.t('usual_sub'));
    var self = this;
    var options = Object.keys(FR_TO_LETTER);
    this.body.innerHTML =
      '<div class="gfq-chips">' + options.map(function (fr) {
        var on = self.answers.usualSize === fr;
        return '<button type="button" class="gfq-chip" data-usual="' + fr + '" aria-pressed="' + on + '">' +
          fr + ' <span class="gfq-sr">/</span> ' + FR_TO_LETTER[fr] + '</button>';
      }).join('') + '</div>' +
      '<p class="gfq-error" data-gfq-error hidden></p>' +
      '<div class="gfq-actions">' +
      '  <button type="button" class="gfq-btn gfq-btn--ghost" data-gfq-back>' + this.t('back') + '</button>' +
      '  <button type="button" class="gfq-btn" data-gfq-next>' + this.t('next') + '</button>' +
      '</div>';

    this.body.querySelectorAll('[data-usual]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.answers.usualSize = btn.getAttribute('data-usual');
        writeProfile({ usualSize: self.answers.usualSize });
        self.body.querySelectorAll('[data-usual]').forEach(function (other) {
          other.setAttribute('aria-pressed', String(other === btn));
        });
      });
    });
    this.body.querySelector('[data-gfq-back]').addEventListener('click', function () { self.go(-1); });
    this.body.querySelector('[data-gfq-next]').addEventListener('click', function () {
      var error = self.body.querySelector('[data-gfq-error]');
      if (!self.answers.usualSize) { error.textContent = self.t('error_usual'); error.hidden = false; return; }
      self.go(1);
    });
  };

  FitQuiz.prototype.render_preference = function () {
    this.setHead(this.t('pref_title'), this.t('pref_sub'));
    var self = this;
    var options = ['fitted', 'regular', 'loose'];
    this.body.innerHTML =
      '<div class="gfq-cards">' + options.map(function (key) {
        return '<button type="button" class="gfq-card" data-pref="' + key + '" aria-pressed="' +
          (self.answers.preference === key) + '"><strong>' + self.t('pref_' + key) + '</strong>' +
          '<span>' + self.t('pref_' + key + '_sub') + '</span></button>';
      }).join('') + '</div>' +
      '<div class="gfq-actions">' +
      '  <button type="button" class="gfq-btn gfq-btn--ghost" data-gfq-back>' + this.t('back') + '</button>' +
      '  <button type="button" class="gfq-btn" data-gfq-next>' + this.t('see_result') + '</button>' +
      '</div>';

    this.body.querySelectorAll('[data-pref]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.answers.preference = btn.getAttribute('data-pref');
        writeProfile({ preference: self.answers.preference });
        self.body.querySelectorAll('[data-pref]').forEach(function (other) {
          other.setAttribute('aria-pressed', String(other === btn));
        });
      });
    });
    this.body.querySelector('[data-gfq-back]').addEventListener('click', function () { self.go(-1); });
    this.body.querySelector('[data-gfq-next]').addEventListener('click', function () { self.go(1); });
  };

  FitQuiz.prototype.render_kids = function () {
    this.setHead(this.t('kids_title'), this.t('kids_sub'));
    var self = this;
    this.body.innerHTML =
      '<div class="gfq-field">' +
      '  <label for="' + this.id + '-kh">' + this.t('kids_height_label') +
      '  <span class="gfq-hint">' + this.t('kids_height_hint') + '</span></label>' +
      '  <div class="gfq-input"><input id="' + this.id + '-kh" type="number" inputmode="numeric" min="70" max="170" step="1" name="kheight">' +
      '  <span class="gfq-unit">cm</span></div>' +
      '</div>' +
      '<p class="gfq-error" data-gfq-error hidden></p>' +
      '<div class="gfq-actions"><button type="button" class="gfq-btn" data-gfq-next>' + this.t('see_result') + '</button></div>';

    this.body.querySelector('[data-gfq-next]').addEventListener('click', function () {
      var value = num(self.body.querySelector('[name="kheight"]').value);
      var error = self.body.querySelector('[data-gfq-error]');
      if (!value || value < 70 || value > 170) { error.textContent = self.t('error_kids'); error.hidden = false; return; }
      self.answers.childHeight = value;
      self.go(1);
    });
  };

  FitQuiz.prototype.buildBody = function () {
    if (this.answers.mode === 'usual' && this.answers.usualSize) {
      var reference = BODY[FR_TO_LETTER[this.answers.usualSize]];
      return reference ? Object.assign({}, reference) : {};
    }
    var body = {};
    if (typeof this.answers.bust === 'number') body.bust = this.answers.bust;
    if (typeof this.answers.waist === 'number') body.waist = this.answers.waist;
    return body;
  };

  FitQuiz.prototype.render_result = function () {
    var result = this.isKids
      ? recommendKidsByHeight(this.chart, this.answers.childHeight)
      : recommend(this.chart, this.buildBody(), this.answers.preference);

    if (!result) {
      this.setHead(this.t('result_none_title'), this.t('result_none_sub'));
      this.body.innerHTML = this.sizeTable(null) +
        '<div class="gfq-actions"><button type="button" class="gfq-btn gfq-btn--ghost" data-gfq-back>' +
        this.t('back') + '</button></div>';
      var self0 = this;
      this.body.querySelector('[data-gfq-back]').addEventListener('click', function () { self0.go(-1); });
      return;
    }

    this.setHead(this.t('result_title'), '');
    var self = this;
    var reasons = [];

    ['bust', 'waist'].forEach(function (dim) {
      if (typeof result.ease[dim] !== 'number') return;
      var ease = Math.round(result.ease[dim] * 10) / 10;
      // "0.0 cm of room" reads badly, so an exact match gets its own sentence.
      if (Math.abs(ease) < 0.5) { reasons.push(self.t('reason_' + dim + '_exact')); return; }
      var word = ease > 0 ? self.t('ease_room') : self.t('ease_stretch');
      reasons.push(self.t('reason_' + dim)
        .replace('%value%', Math.abs(ease).toFixed(1))
        .replace('%word%', word));
    });
    if (typeof result.row.length === 'number') {
      reasons.push(this.t('reason_length').replace('%value%', result.row.length));
    }
    if (result.alternative) {
      reasons.push(this.t('reason_between').replace('%size%', result.alternative));
    }

    this.body.innerHTML =
      '<div class="gfq-result">' +
      '  <p class="gfq-eyebrow">' + this.t('result_your_size') + '</p>' +
      '  <p class="gfq-result-size">' + result.size + '</p>' +
      '  <p class="gfq-result-note">' + this.t('result_note') + '</p>' +
      '  <span class="gfq-conf" data-level="' + (result.confidence === 'high' ? 'high' : 'low') + '">' +
         this.t('confidence_' + result.confidence) + '</span>' +
      '</div>' +
      (reasons.length ? '<ul class="gfq-reasons"><li>' + reasons.join('</li><li>') + '</li></ul>' : '') +
      this.sizeTable(result.size) +
      this.howTo() +
      '<div class="gfq-actions">' +
      '  <button type="button" class="gfq-btn gfq-btn--ghost" data-gfq-back>' + this.t('restart') + '</button>' +
      '  <button type="button" class="gfq-btn" data-gfq-pick>' + this.t('choose').replace('%size%', result.size) + '</button>' +
      '</div>' +
      '<p class="gfq-disclaimer">' + this.t('disclaimer') + '</p>';

    this.body.querySelector('[data-gfq-back]').addEventListener('click', function () {
      self.stepIndex = 0; self.render();
    });
    this.body.querySelector('[data-gfq-pick]').addEventListener('click', function () {
      self.selectVariant(result.size);
      self.close();
    });
  };

  FitQuiz.prototype.sizeTable = function (highlight) {
    var dims = ['bust', 'waist', 'shoulders', 'sleeve', 'length'].filter(function (dim) {
      return this.chart.sizes.some(function (row) { return typeof row[dim] === 'number'; });
    }, this);
    var self = this;
    var head = '<tr><th>' + this.t('col_size') + '</th>' +
      dims.map(function (dim) { return '<th>' + self.t('col_' + dim) + '</th>'; }).join('') + '</tr>';
    var rows = this.chart.sizes.map(function (row) {
      return '<tr data-pick="' + (row.label === highlight) + '"><td>' + row.label + '</td>' +
        dims.map(function (dim) {
          return '<td>' + (typeof row[dim] === 'number' ? row[dim] : '–') + '</td>';
        }).join('') + '</tr>';
    }).join('');
    return '<div class="gfq-tablewrap"><table class="gfq-table"><caption class="gfq-sr">' +
      this.t('table_caption') + '</caption><thead>' + head + '</thead><tbody>' + rows + '</tbody></table></div>';
  };

  FitQuiz.prototype.howTo = function () {
    if (!this.chart.howto || !this.chart.howto.length) return '';
    return '<details class="gfq-details"><summary>' + this.t('howto') + '</summary><p>' +
      this.chart.howto.join('<br>') + '</p></details>';
  };

  /* Select the recommended size in the product form on the page. */
  FitQuiz.prototype.selectVariant = function (label) {
    var scope = document.querySelector('product-info') || document;
    var target = normaliseSize(label);
    var radios = scope.querySelectorAll('input[type="radio"]');
    for (var i = 0; i < radios.length; i++) {
      if (normaliseSize(radios[i].value) === target) {
        var control = radios[i].id ? scope.querySelector('label[for="' + radios[i].id + '"]') : null;
        if (control) control.click(); else radios[i].click();
        radios[i].scrollIntoView({ block: 'center', behavior: 'smooth' });
        return true;
      }
    }
    var selects = scope.querySelectorAll('select');
    for (var j = 0; j < selects.length; j++) {
      var options = selects[j].options;
      for (var k = 0; k < options.length; k++) {
        if (normaliseSize(options[k].value) === target) {
          selects[j].selectedIndex = k;
          selects[j].dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
      }
    }
    return false;
  };

  /*
   * The theme replaces chunks of the product page on every variant change, so we
   * never hold on to a button: clicks are delegated from the document and the quiz
   * instance is looked up (or rebuilt) from whatever markup is on the page now.
   */
  var instances = {};

  function instanceFor(root) {
    if (!root.id) root.id = 'gfq-' + Math.random().toString(36).slice(2, 8);
    var existing = instances[root.id];
    if (existing && existing.root.isConnected) return existing;
    var quiz = new FitQuiz(root);
    instances[root.id] = quiz;
    return quiz;
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-gfq-open]');
    if (!trigger) return;
    var root = trigger.closest('[data-fit-quiz]');
    if (!root) return;
    event.preventDefault();
    try { instanceFor(root).open(); }
    catch (error) { root.hidden = true; }
  });
})();
